import { createContext, useContext } from 'react';
import { Helmet } from 'react-helmet';
import AppPaths from '../../AppPaths';
import { useLocale } from '../../contexts/LocaleContext';
import buildShopProductsJsonLd, { ShopProductForJsonLd } from '../../lib/shopProductsJsonLd';
import * as types from '../../types';

interface ShopProductsJsonLdProps {
  products: ShopProductForJsonLd[];
  discountsByKey: Record<string, types.HardwareDiscountPreview | undefined>;
}

/** True where the document already carries the shop's ItemList (main prerenders it), so a second would duplicate it. */
export const StaticShopProductsJsonLdContext = createContext(false);

/** Product JSON-LD for the pick-your-fi shop; see `buildShopProductsJsonLd` for what it publishes and why. */
export default function ShopProductsJsonLd({ products, discountsByKey }: ShopProductsJsonLdProps) {
  const { currency, country } = useLocale();
  const inDocument = useContext(StaticShopProductsJsonLdContext);
  if (typeof window === 'undefined' || inDocument) {
    return null;
  }
  // Anything after `/shop` (a trailing slash, a sub-path legacy's prefix route still matches) is not
  // the shop's URL; anything before it is main's language tree (`/de/shop`).
  const shopPath = window.location.pathname.replace(/\/shop(\/.*)?$/i, AppPaths.Shop);
  const jsonLd = buildShopProductsJsonLd(products, discountsByKey, currency, country, window.location.origin, shopPath);
  if (!jsonLd) {
    return null;
  }
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}
